import type { ReactNode } from "react";
import { ButtonGroup } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";

type DocumentToolbarGroupProps = {
  /** What the group is called — the same word its folded menu carries. */
  label: string;
  /**
   * Renders the children as one joined segment. Turned off for groups that
   * hold a text field, which reads as a field and not as a button.
   */
  segmented?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * One named segment of the document viewer toolbar.
 *
 * The group, not the single button, is the unit that folds: when a group runs
 * out of room it collapses into a menu named after itself, so nothing ends up
 * behind a shared "…" that says nothing about what is inside it.
 */
export const DocumentToolbarGroup = ({
  label,
  segmented = true,
  className,
  children,
}: DocumentToolbarGroupProps) =>
  segmented ? (
    <ButtonGroup
      aria-label={label}
      // The group only caps its right end on its own; without this the left
      // end keeps the child's own 8px radius and the segment looks lopsided.
      // Note it aligns `stretch`, not `center`: a segment one size taller than
      // the ones beside it reads as a separate box sitting on top of them.
      className={cn("[&>*:first-child]:rounded-l-4xl", className)}
    >
      {children}
    </ButtonGroup>
  ) : (
    <div
      role="group"
      aria-label={label}
      className={cn("flex items-center gap-1", className)}
    >
      {children}
    </div>
  );

export default DocumentToolbarGroup;
